import React, { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import fuelApi from '@/services/fuelApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

// AdjustSaldoDialog — ajuste manual do saldo do tanque (saldo_override).
// Ação administrativa (menu "⋯" da FuelControlPage — backlog 3.1). O motivo é
// OBRIGATÓRIO (D-37: auditoria do ajuste); o valor vira a nova âncora de saldo
// no servidor (INV-FUEL-SERVER-AUTHORITATIVE-001).

// Formata o que o operador digitou (litros) — sem conversão de unidade na tela.
const fmtLitros = (l) =>
  l == null || Number.isNaN(l) ? '—' : `${Math.round(l).toLocaleString('pt-BR')} L`;

export default function AdjustSaldoDialog({ open, onOpenChange, equipmentId, equipmentLabel, onSuccess }) {
  const [litros, setLitros] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  async function handleSubmit(e) {
    e.preventDefault();
    const l = parseFloat(litros);
    if (!Number.isFinite(l) || l < 0) {
      toast.error('Informe o saldo em litros (≥ 0).');
      return;
    }
    const motivo = reason.trim();
    if (!motivo) {
      toast.error('Informe o motivo do ajuste (obrigatório).');
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);

    try {
      const ts = new Date().toISOString();
      await fuelApi.createFuelRecord({
        equipment_id: equipmentId,
        type: 'saldo_override',
        timestamp: ts,
        // Litros digitados; L → m³ é feito no backend (registry).
        saldo_l_after: l,
        source: 'manual',
        notes: `Ajuste de saldo: ${fmtLitros(l)}`,
        reason: motivo,
        client_uuid: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID() : `override-${ts}-${l}`,
      });
      toast.success(`Saldo ajustado: ${fmtLitros(l)}`);
      setLitros('');
      setReason('');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Falha ao ajustar saldo.');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="adjust-saldo-dialog">
        <DialogHeader>
          <DialogTitle>Ajustar saldo do tanque</DialogTitle>
          <DialogDescription>
            {equipmentLabel ? `Embarcação: ${equipmentLabel}. ` : ''}
            Define uma nova âncora de saldo — use para correções auditadas (medição física, sonda).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="adjust-litros">Saldo atual do tanque (litros) *</Label>
            <Input
              id="adjust-litros"
              data-testid="adjust-litros"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              placeholder="ex.: 9178"
              value={litros}
              onChange={(e) => setLitros(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjust-reason">Motivo do ajuste *</Label>
            <Textarea
              id="adjust-reason"
              data-testid="adjust-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex.: medição física do tanque em 04/07"
              rows={2}
              required
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || !equipmentId} data-testid="adjust-submit">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ajustar saldo
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
